// src/components/cards/ProductCard.js
import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, CheckCircle, ImageOff } from 'lucide-react';

/**
 * ProductCard component displays details for a single product.
 * It's designed to be animated by its parent container using Framer Motion variants.
 * @param {object} props - The component props.
 * @param {object} props.product - The product data to display.
 * @param {object} props.variants - The animation variants passed from the parent.
 * @return {React.ReactElement} The ProductCard component.
 */
const ProductCard = ({ product, variants }) => {
  return (
    <motion.div
      variants={variants}
      className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300"
    >
      <div className="w-72 h-72 mb-6 mx-auto overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name || 'Product image'}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = e.target.nextElementSibling;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          style={{ display: product.image ? 'none' : 'flex' }}
          className="text-gray-400 text-sm flex-col items-center justify-center"
        >
          <ImageOff className="w-16 h-16 mb-2" />
          No image
        </div>
      </div>
      <h3 className="text-xl font-bold text-gray-800 mb-4 hover:text-orange-500 transition-colors">
        {product.name}
      </h3>

      <div className="space-y-3 mb-6 text-gray-600">
        <div className="flex justify-between">
          <span className="text-base">Грузоподъемность:</span>
          <span className="font-semibold text-orange-500 text-lg">{product.capacity}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-base">Ширина:</span>
          <span className="font-semibold text-orange-500 text-lg">{product.width}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-base">Длина:</span>
          <span className="font-semibold text-orange-500 text-lg">{product.length}</span>
        </div>
      </div>

      <div className="space-y-2 mb-6">
        {product.features.map((feature, i) => (
          <div key={i} className="flex items-center gap-2 text-gray-600">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm">{feature}</span>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center">
        <span className="text-2xl font-bold text-orange-500">{product.price}</span>
        <a
          href="https://api.whatsapp.com/send?phone=77785929644"
          target="_blank" // Opens the link in a new tab
          rel="noopener noreferrer" // Recommended for security when using target="_blank"
          className="bg-orange-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-orange-600 transition-colors"
        >
          Заказать <ChevronRight className="w-4 h-4" />
        </a>
      </div>
    </motion.div>
  );
};

export default ProductCard;
